import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch, useStore } from "react-redux";
import textImage from "../../assets/time-img.png";
import clockTimer from "../../assets/clock-timer-img.png";
import backgroundImg from "../../assets/Background.png";
import attentionImg from "../../assets/attentionimg.png";
import BrandLoader from "../../common/components/BrandLoader";
import { STEP_COPY_BY_REFERENCE, TEXT_CONTENT, ERROR_MESSAGES, SUCCESS_MESSAGES, JOURNEY_TYPES, getJourneyPlanName, getIncomeRangeFromValue, getTermJourneyCanonicalUrl } from "../../constants";
import "./Lead.scss";
import {
  fetchFormDefinition,
  preloadPincodeData,
  submitFormData,
} from "../../api/formService";
import {
  DynamicFormField,
  StepNavigation,
  Modal,
} from "../../common/components";
import {
  validateForm,
  validateFieldByName,
  extractApplicationNumberFromResponse,
  extractCaseIdFromResponse,
  getStoredApplicationNumber,
  setStoredApplicationNumber,
  setStoredCaseId,
} from "../../common/utils";
import { transformFormDefinition } from "../../common/utils/formParser";
import { calculateAge, isDobWithinAgeRange } from "../../common/utils/formatters";
import { useJourney } from "../../common/hooks/useJourney";
import { journeyActions } from "../../store/journeySlice";
import EditStepModal from "../../common/components/EditStepModal";
import AttentionModal from "../../common/components/AttentionModal";
import { getJourneyData } from "../../api/journeyService";
import {
  decryptJourneyQueryValue,
  encryptJourneyQueryValue,
} from "../../common/utils/journeyEncryption";
import {
  hydrateStoreFromJourney,
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
  leadStageToStepIndex,
} from "../../common/utils/journeyResume";
import { mergeCumulativeStagesOrdered } from "../../common/utils/journeyLeadFields";
import {
  PRIMARY_ANNUITANT_FORM_TO_PERSIST,
  PROPOSER_BASIC_FORM_TO_PERSIST,
  PROPOSER_PERSONAL_FORM_TO_PERSIST,
  remapFields,
} from "../../common/utils/nischitRoleFields";
import { addDobUpdateMetaIfRequired, resolveLeadDob, syncOwnershipDobInSession } from "../../common/utils/journeyOwnershipDob";
import { scrollToTopOnMount } from "../../common/utils/scrollToTop";
import {
  ANNUAL_INCOME_FIELD_NAME,
  getAnnualIncomeError,
} from "../../common/utils/annualIncomeValidation";
import {
  GENDER_RESTRICTED_AGE_ERROR,
  violatesGenderRestrictedAge,
} from "../../common/utils/genderAgeRestriction";

const SESSION_FORM_KEY = "inlic_lead_form_data";

const ANNUITANT_MIN_AGE = 30;
const ANNUITANT_MAX_AGE = 75;
const ANNUITANT_AGE_ERROR = `Minimum age at entry of Primary Annuitant should be 30 years. Please select other member, or increase the age to proceed.`;
const NISCHIT_LEAD_STAGE_BY_REFERENCE = {
  basic: "lead_basic",
  personal: "lead_personal",
  annuitant: "lead_primary_annuitant",
  lifeassured: "lead_primary_annuitant",
  residency: "lead_residency",
  additional: "lead_additional",
};
const NISCHIT_LEAD_STAGE_BY_INDEX = [
  "lead_basic",
  "lead_personal",
  "lead_primary_annuitant",
  "lead_residency",
  "lead_additional",
];

const INDIA_RESIDENT_FIELDS = [
  "basedInIndia",
  "nationality",
  "nriResident",
  "isIndiaResident",
  "areYouBasedInIndia",
];

const params = new URLSearchParams(window.location.search);

const utmSourceParam = params.get("utm_source");
const isCrosssellSource = (value) => {
  const source = String(value || "").trim().toLowerCase();
  return source === "mbsb_cross-sell" || source === "dm_crosssell";
};
const utmMediumParam = params.get("utm_medium");
const utmCampaignParam = params.get("utm_campaign");

const isIndiaResidentField = (name) => INDIA_RESIDENT_FIELDS.includes(name);
const isNoValue = (v) =>
  v &&
  ["no", "false", "nri", "non-resident"].includes(
    String(v).toLowerCase().trim(),
  );

const resolveIsSinglePayValue = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    if (typeof candidate === "string" && candidate.trim() === "") continue;
    return candidate;
  }
  return false;
};

const isBlankValue = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const normalizeFieldToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const isFieldHidden = (field) =>
  field?.hideField === true || field?.hidden === true;

const CONSENT_TERMS_REFERENCE_KEYS = new Set([
  normalizeFieldToken("Consent To Terms and Condition"),
  normalizeFieldToken("consentToTerms"),
]);

const isConsentTermsField = (field) => {
  const ref = normalizeFieldToken(field?.fieldReference);
  const name = normalizeFieldToken(field?.name || field?.fieldName);
  return (
    CONSENT_TERMS_REFERENCE_KEYS.has(ref) ||
    CONSENT_TERMS_REFERENCE_KEYS.has(name)
  );
};

const initializeFormData = (definition) => {
  const data = {};
  if (!definition?.fieldGroups) return data;
  definition.fieldGroups.forEach((group) => {
    (group.fields || []).forEach((field) => {
      const name = field.fieldReference || field.name || field.fieldName;
      if (!name) return;
      data[name] = isConsentTermsField(field)
        ? false
        : field.defaultValue ?? (field.type === "checkbox" ? false : "");
    });
  });
  return data;
};

const shouldRenderDynamicField = (field) =>
  !isFieldHidden(field) && !isConsentTermsField(field);

const getConsentTermsFieldMeta = (definition) => {
  const fields =
    definition?.fieldGroups?.flatMap((group) => group?.fields || []) || [];
  const match = fields.find((field) => isConsentTermsField(field));
  if (!match) return { fieldName: null, isHidden: false, required: false };
  return {
    fieldName: match.fieldReference || match.name || match.fieldName,
    isHidden: isFieldHidden(match),
    required: Boolean(match.required ?? match.validation?.required),
  };
};

const mergeWithoutBlankOverwrite = (base = {}, incoming = {}) => {
  const next = { ...base };
  Object.keys(incoming || {}).forEach((key) => {
    const incomingValue = incoming[key];
    const baseValue = next[key];
    if (isBlankValue(incomingValue) && !isBlankValue(baseValue)) return;
    next[key] = incomingValue;
  });
  return next;
};

const parseJsonObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const getStepTitle = (stepIndex, stepLabel) => {
  if (stepIndex === 0) return stepLabel;
  if (stepIndex === 1) return TEXT_CONTENT.PERSONALIZE_TEXT
  if (stepIndex === 2) return TEXT_CONTENT.LOCATION
  if (stepIndex === 3) return TEXT_CONTENT.CHECK
  return stepLabel;
};

const getButtonText = (submitting, isLastStep) => {
  if (submitting) return "Submitting...";
  if (isLastStep) return "Reveal My Personalised Plan";
  return "Continue";
};

const normalizeStepReference = (value) => normalizeFieldToken(value);


const getCurrentStepCopy = (fieldGroup, fallbackLabel = "") => {
  const referenceKey = normalizeStepReference(
    fieldGroup?.fieldReference || fieldGroup?.name || fieldGroup?.title,
  );
  const mapped = STEP_COPY_BY_REFERENCE[referenceKey];
  if (mapped) return { referenceKey, ...mapped };
  return { referenceKey, title: fallbackLabel, subtitle: "", bubble: "" };
};

const isAnnuitantStep = (group) => {
  const token = normalizeStepReference(
    group?.fieldReference || group?.name || group?.title,
  );
  // Support both legacy key ("annuitant") and ("lifeassured").
  return token === "annuitant" || token === "lifeassured"
};

const isResidencyLeadStep = (fieldGroup) =>
  getCurrentStepCopy(fieldGroup).referenceKey === ("residency");

const resolveNischitLeadStage = (stepIndex, fieldGroup, fallbackStage) => {
  const referenceKey = getCurrentStepCopy(fieldGroup).referenceKey;
  if (NISCHIT_LEAD_STAGE_BY_REFERENCE[referenceKey]) {
    return NISCHIT_LEAD_STAGE_BY_REFERENCE[referenceKey];
  }
  if (isAnnuitantStep(fieldGroup)) {
    return "lead_primary_annuitant";
  }
  return (
    NISCHIT_LEAD_STAGE_BY_INDEX[stepIndex] ||
    fallbackStage ||
    "lead_additional"
  );
};

const addLeadOwnershipProof = (payload = {}, sourceData = {}, effectiveAppNumber = null) =>
  addDobUpdateMetaIfRequired(payload, sourceData, effectiveAppNumber);

const getFieldToken = (field) =>
  normalizeFieldToken(field?.fieldReference || field?.name || field?.fieldName);

const isRelationshipField = (field) =>
  getFieldToken(field).includes("relationship");

const isGenderField = (field) => getFieldToken(field).includes("gender");
const isNameField = (field) => getFieldToken(field).includes("name");


const isDobField = (field) => {
  const token = getFieldToken(field);
  return (
    token.includes("dateofbirth") ||
    token.endsWith("dob") ||
    token.includes("dob")
  );
};

const isSinglePayField = (field) => getFieldToken(field) === "issinglepay";

const isEnableSinglePayConditionField = (field) =>
  getFieldToken(field) === "enablesinglepaycondition";

const getFieldValue = (data, field) => {
  const key = field?.fieldReference || field?.name || field?.fieldName;
  return key ? data?.[key] : undefined;
};

const resolveRelationshipReferenceValue = (stepGroup, rawValue) => {
  if (rawValue == null) return rawValue;
  const relationshipField = (stepGroup?.fields || []).find(isRelationshipField);
  const options = Array.isArray(relationshipField?.options)
    ? relationshipField.options
    : [];
  const normalizedRaw = String(rawValue).trim().toLowerCase();
  const matchedOption = options.find((opt) => {
    const value = String(opt?.value ?? "")
      .trim()
      .toLowerCase();
    const reference = String(opt?.reference ?? "")
      .trim()
      .toLowerCase();
    const label = String(opt?.label?.en_US ?? opt?.label ?? "")
      .trim()
      .toLowerCase();
    return (
      normalizedRaw === value ||
      normalizedRaw === reference ||
      normalizedRaw === label
    );
  });
  return matchedOption?.reference ?? rawValue;
};

const resolveStepRelationshipValue = (formData, stepGroup) => {
  const relationshipField = (stepGroup?.fields || []).find(isRelationshipField);
  if (!relationshipField) return undefined;
  const rawValue = getFieldValue(formData, relationshipField);
  return resolveRelationshipReferenceValue(stepGroup, rawValue);
};

const getRelationshipFieldName = (stepGroup) => {
  const relationshipField = (stepGroup?.fields || []).find(isRelationshipField);
  return (
    relationshipField?.fieldReference ||
    relationshipField?.name ||
    relationshipField?.fieldName ||
    null
  );
};

/** Liferay option label may be a string or localized object. */
const getLocalizedOptionLabel = (opt) => {
  if (opt == null) return "";
  if (typeof opt === "string") return String(opt).trim();
  const lab = opt.label;
  if (lab && typeof lab === "object") {
    return String(lab.en_US ?? lab.en ?? Object.values(lab)[0] ?? "").trim();
  }
  return String(lab ?? opt.name ?? "").trim();
};

const findLeadFieldDefinitionByDataKey = (definition, dataKey) => {
  if (!definition?.fieldGroups || !dataKey) return null;
  for (const group of definition.fieldGroups) {
    for (const field of group.fields || []) {
      const key = field.fieldReference || field.name || field.fieldName;
      if (key === dataKey) return field;
    }
  }
  return null;
};

/**
 * Map UI / session values to the human-readable option label sent in submitLead
 * (see buildPayloadForCurrentStep). Keeps Redux leadFormData aligned with API.
 * Prefer label (e.g. "Female", "Salaried") over DDM Option* values / references.
 */
const mapOptionRawValueToReference = (field, rawValue) => {
  if (!field?.options?.length) return rawValue;
  if (rawValue === undefined || rawValue === null || rawValue === "")
    return rawValue;

  const rawStr = String(rawValue).trim();
  const normalizedRaw = rawStr.toLowerCase();

  const selectedOption = field.options.find((opt) => {
    const value = String(opt?.value ?? "").trim();
    const reference = String(opt?.reference ?? "").trim();
    const label = getLocalizedOptionLabel(opt).toLowerCase();
    return (
      rawStr === value ||
      rawStr === reference ||
      normalizedRaw === label ||
      normalizedRaw === value.toLowerCase() ||
      normalizedRaw === reference.toLowerCase()
    );
  });

  if (!selectedOption) return rawValue;

  const label = getLocalizedOptionLabel(selectedOption);
  if (label) return label;

  if (
    selectedOption.reference != null &&
    String(selectedOption.reference).trim() !== ""
  ) {
    return selectedOption.reference;
  }
  return rawValue;
};

const normalizeLeadFormDataOptionReferences = (definition, data) => {
  if (!definition?.fieldGroups || !data || typeof data !== "object")
    return data;
  const out = { ...data };
  for (const key of Object.keys(out)) {
    const field = findLeadFieldDefinitionByDataKey(definition, key);
    out[key] = mapOptionRawValueToReference(field, out[key]);
  }
  return out;
};

/**
 * True when annuitant relationship is "self" — works whether Redux stores the literal
 * `self`, a display label, or the Liferay option `reference` (after normalization).
 */
const isAnnuitantSelfRelationship = (formData, stepGroup) => {
  if (!isAnnuitantStep(stepGroup)) return false;
  const resolved = resolveStepRelationshipValue(formData, stepGroup);
  if (resolved == null || resolved === "") return false;
  if (String(resolved).trim().toLowerCase() === "self") return true;

  const relField = (stepGroup?.fields || []).find(isRelationshipField);
  const selfOption = (relField?.options || []).find((opt) => {
    const lab =
      typeof opt === "string"
        ? opt.trim().toLowerCase()
        : getLocalizedOptionLabel(opt).trim().toLowerCase();
    return lab === "self";
  });
  if (!selfOption || typeof selfOption !== "object") return false;
  const selfRef = selfOption.reference;
  if (selfRef == null || String(selfRef).trim() === "") return false;
  return String(resolved).trim() === String(selfRef).trim();
};

const getDobFieldName = (stepGroup) => {
  const dobField = (stepGroup?.fields || []).find(isDobField);
  return (
    dobField?.fieldReference || dobField?.name || dobField?.fieldName || null
  );
};

const resolvePreviousStepValueByMatcher = (
  formData,
  previousGroup,
  matcher,
) => {
  const field = (previousGroup?.fields || []).find(matcher);
  return field ? getFieldValue(formData, field) : undefined;
};

const getPrimaryLifeAssuredGender = (data) => {
  const source = data && typeof data === "object" ? data : {};
  return source.gender || source.lifeAssuredGender || source.proposerGender || "";
};

const getPrimaryLifeAssuredDob = (data) => {
  const source = data && typeof data === "object" ? data : {};
  return source.dateOfBirth || source.lifeAssuredDob || source.proposerDob || "";
};

const getPrimaryLifeAssuredName = (data) => {
  const source = data && typeof data === "object" ? data : {};
  return source.name || source.Fullname || source.fullName || source.proposerName || "";
};

/** Age gate for Single Pay uses annuitant DOB; when relationship is self, annuitant age matches primary life assured. */
const getAnnuitantDobForSinglePayEligibility = (formData, formDefinition) => {
  const annuitantGroup = formDefinition?.fieldGroups?.find(isAnnuitantStep);
  if (!annuitantGroup) return getPrimaryLifeAssuredDob(formData);
  if (isAnnuitantSelfRelationship(formData, annuitantGroup)) {
    return getPrimaryLifeAssuredDob(formData);
  }
  const dobField = (annuitantGroup.fields || []).find(isDobField);
  const key = dobField?.fieldReference || dobField?.name || dobField?.fieldName;
  if (key && formData?.[key]) return formData[key];
  return formData?.annuitantDateOfBirth || "";
};

/**
 * "Other"/"Others" gender primary annuitant must be 30 to 45y364d. For a Self
 * annuitant the proposer's gender and DOB are the annuitant's.
 */
const isPrimaryAnnuitantGenderAgeBlocked = (formData, formDefinition) => {
  const groups = formDefinition?.fieldGroups || [];
  const annuitantGroup = groups.find(isAnnuitantStep);
  if (!annuitantGroup) return false;
  const annuitantFields = annuitantGroup.fields || [];

  if (isAnnuitantSelfRelationship(formData, annuitantGroup)) {
    const proposerGenderField = groups
      .filter((group) => !isAnnuitantStep(group))
      .flatMap((group) => group.fields || [])
      .find(isGenderField);
    return violatesGenderRestrictedAge(
      getFieldValue(formData, proposerGenderField) ||
        getPrimaryLifeAssuredGender(formData),
      getPrimaryLifeAssuredDob(formData),
      proposerGenderField,
    );
  }

  const genderField = annuitantFields.find(isGenderField);
  const dobField = annuitantFields.find(isDobField);
  return violatesGenderRestrictedAge(
    getFieldValue(formData, genderField) ||
      formData?.annuitantGender ||
      formData?.primaryAnnuitantGender,
    getFieldValue(formData, dobField) ||
      formData?.annuitantDateOfBirth ||
      formData?.primaryAnnuitantDob,
    genderField,
  );
};

const getAnnuitantAgeValidation = (formData, stepGroup) => {
  if (!isAnnuitantStep(stepGroup)) return null;

  const isAnnuitantSelf = isAnnuitantSelfRelationship(formData, stepGroup);
  const relationshipFieldName = getRelationshipFieldName(stepGroup);
  const dobFieldName = getDobFieldName(stepGroup);
  const annuitantDob = isAnnuitantSelf
    ? getPrimaryLifeAssuredDob(formData)
    : dobFieldName
      ? formData?.[dobFieldName]
      : formData?.annuitantDateOfBirth;

  if (isBlankValue(annuitantDob)) return null;

  const age = calculateAge(annuitantDob);
  if (
    age === null ||
    !isDobWithinAgeRange(annuitantDob, {
      minAge: ANNUITANT_MIN_AGE,
      maxAge: ANNUITANT_MAX_AGE,
    })
  ) {
    const fieldName = isAnnuitantSelf
      ? relationshipFieldName || dobFieldName
      : dobFieldName || relationshipFieldName;
    if (!fieldName) return null;
    return {
      fieldName,
      message: ANNUITANT_AGE_ERROR,
    };
  }

  return null;
};



const incomeValidation = (formData) => {
  const message = getAnnualIncomeError(formData?.[ANNUAL_INCOME_FIELD_NAME]);
  if (!message) return null;
  return { fieldName: ANNUAL_INCOME_FIELD_NAME, message };
};

const resolveSinglePayThreshold = (formDefinition, formData) => {
  const fields =
    formDefinition?.fieldGroups?.flatMap((group) => group?.fields || []) || [];
  const conditionField = fields.find(isEnableSinglePayConditionField);
  if (!conditionField) return null;
  const key =
    conditionField.fieldReference ||
    conditionField.name ||
    conditionField.fieldName;
  const fromData = key ? formData?.[key] : null;
  const fromDefault = conditionField.defaultValue;
  const raw = fromData ?? fromDefault;
  const threshold = Number(raw);
  return Number.isFinite(threshold) ? threshold : null;
};

const shouldShowSinglePayField = (formDefinition, formData) => {
  const threshold = resolveSinglePayThreshold(formDefinition, formData);
  if (!Number.isFinite(threshold)) return false;
  const annuitantDob = getAnnuitantDobForSinglePayEligibility(
    formData,
    formDefinition,
  );
  const age = calculateAge(annuitantDob);
  return age !== null && age >= threshold;
};

const shouldIncludeDynamicField = (field, formDefinition, formData) => {
  if (!shouldRenderDynamicField(field)) return false;
  if (isSinglePayField(field)) {
    return shouldShowSinglePayField(formDefinition, formData);
  }
  return true;
};

const normalizeStageToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const parseMaybeJsonObject = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const resolveLeadStepIndexFromJourney = (journey, fieldGroups = []) => {
  if (!Array.isArray(fieldGroups) || fieldGroups.length === 0) return 1;

  const tokenToIndex = new Map();
  fieldGroups.forEach((group, index) => {
    [group?.fieldReference, group?.name, group?.title, group?.label].forEach(
      (candidate) => {
        const token = normalizeStageToken(candidate);
        if (token) tokenToIndex.set(token, index);
      },
    );
  });

  const stageLikeData =
    parseMaybeJsonObject(journey?.stages) ||
    parseMaybeJsonObject(journey?.currentStageData) ||
    parseMaybeJsonObject(journey?.c_currentStageData) ||
    null;

  const candidates = [
    journey?.currentStage,
    journey?.current_stage,
    journey?.c_currentStage,
    journey?.currentStageNumber,
  ];

  if (stageLikeData && typeof stageLikeData === "object") {
    Object.keys(stageLikeData).forEach((key) => candidates.push(key));
    Object.values(stageLikeData).forEach((value) => {
      if (value && typeof value === "object") {
        candidates.push(value.currentStage, value.fieldReference);
      }
    });
  }

  let bestIndex = -1;
  candidates.forEach((candidate) => {
    const token = normalizeStageToken(candidate);
    if (!token) return;
    if (tokenToIndex.has(token)) {
      bestIndex = Math.max(bestIndex, tokenToIndex.get(token));
      return;
    }
    // Allow partial matches for strings like "Stage 1.0 Lead".
    tokenToIndex.forEach((index, refToken) => {
      if (token.includes(refToken) || refToken.includes(token)) {
        bestIndex = Math.max(bestIndex, index);
      }
    });
  });

  if (bestIndex >= 0) {
    if (bestIndex === 0 && fieldGroups.length > 1) return 1;
    return bestIndex;
  }
  const fallbackStage =
    journey?.currentStage ??
    journey?.current_stage ??
    journey?.c_currentStage ??
    journey?.currentStageNumber;
  const fallbackIndex = leadStageToStepIndex(fallbackStage, fieldGroups);
  if (fallbackIndex === 0 && fieldGroups.length > 1) return 1;
  return fallbackIndex;
};

const InsuranceJourneyLead = ({ mode = "basic", journeyType }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduxDispatch = useDispatch();
  const store = useStore();
  const { handleLeadComplete, saveStepData } = useJourney();
  const { applicationNumber: storedApplicationNumber, caseId: storedCaseId } =
    useSelector((s) => s.journey);

  const [formDefinition, setFormDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  /** True until initial fetchFormDefinition (+ optional journey hydrate) finishes. */
  const [initializingForm, setInitializingForm] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(mode === "basic" ? 0 : 1);
  const [completedSteps, setCompletedSteps] = useState(
    mode === "personalize" ? [0] : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState(null);
  const [apiErrorMsg, setApiErrorMsg] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAgeCriteriaModal, setShowAgeCriteriaModal] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const journeyPlanName = getJourneyPlanName(journeyType);
  /** When user picks “No” for India-based, OK keeps it; Cancel/backdrop restores this snapshot. */
  const attentionModalRevertRef = useRef(null);
  const [editStepIndex, setEditStepIndex] = useState(null);
  const [, setIsChecked] = useState(false);
  const [residencyPincodeNoResults, setResidencyPincodeNoResults] =
    useState(false);
  const stepData = useSelector((s) => s.journey.stepData);
  const leadFormData = useSelector((state) => state.journey.leadFormData);
  const leadResult = useSelector((state) => state.journey.leadResult);

  const handlePincodeSuggestionsChange = useCallback(({ resultCount }) => {
    if (resultCount === null || resultCount === undefined) {
      setResidencyPincodeNoResults(false);
      return;
    }
    setResidencyPincodeNoResults(resultCount === 0);
  }, []);

  const syncAndRedirectIfJourneyAdvanced = useCallback(
    async (effectiveAppNum, dateOfBirth) => {
      if (!effectiveAppNum) return false;
      try {
        const latestJourney = await getJourneyData(effectiveAppNum, dateOfBirth || null);
        if (!latestJourney || typeof latestJourney !== "object") return false;

        hydrateStoreFromJourney(
          reduxDispatch,
          latestJourney,
          effectiveAppNum,
          store.getState,
        );
        const resumeRoute = getResumeRouteFromJourney(latestJourney);
        if (!resumeRoute) return false;

        const resumeState =
          resumeRoute === "/proposal"
            ? getResumeStateFromJourney(latestJourney)
            : null;
        navigate(resumeRoute, resumeState ? { state: resumeState } : {});
        return true;
      } catch {
        // Best-effort safety check only. If this check fails, continue normal submit.
        return false;
      }
    },
    [navigate, reduxDispatch, store],
  );

  useEffect(() => {
    const loadForm = async () => {
      try {
        const queryApplicationNumberRaw =
          searchParams.get("applicationNumber")?.trim() || null;
        const queryApplicationNumber = queryApplicationNumberRaw
          ? decryptJourneyQueryValue(queryApplicationNumberRaw) ||
          queryApplicationNumberRaw
          : null;
        const persistedAppNumber =
          queryApplicationNumber ||
          storedApplicationNumber ||
          getStoredApplicationNumber();
        const reduxDob = resolveLeadDob(leadFormData);
        let sessionDob = null;
        try {
          const rawSessionLead = sessionStorage.getItem(SESSION_FORM_KEY);
          if (rawSessionLead) {
            const parsedSessionLead = JSON.parse(rawSessionLead);
            sessionDob = resolveLeadDob(parsedSessionLead);
          }
        } catch {
          sessionDob = null;
        }
        const urlDob = searchParams.get("dateOfBirth")?.trim() || null;
        const resumeDob = reduxDob || sessionDob || urlDob || null;

        // For existing journeys, ignore any stale session snapshot
        if (persistedAppNumber && !sessionDob) {
          try {
            sessionStorage.removeItem(SESSION_FORM_KEY);
          } catch {
            /* ignore */
          }
        }

        preloadPincodeData().catch(() => { });

        const raw = await fetchFormDefinition("lead");
        const def = transformFormDefinition(raw);
        setFormDefinition(def);
        reduxDispatch(journeyActions.setFormDefinition(def));

        const initialData = initializeFormData(def);
        let nextFormData = { ...initialData };

        // merge redux stepData (persisted)
        if (stepData && Object.keys(stepData).length > 0) {
          const mergedStepData = Object.values(stepData).reduce((acc, step) => {
            return { ...acc, ...step };
          }, {});
          nextFormData = { ...nextFormData, ...mergedStepData };
        }

        if (persistedAppNumber) {
          // Journey continuation: prefill lead and optionally resume past lead or restore step
          try {
            const journey = await getJourneyData(persistedAppNumber, resumeDob);

            if (journey && typeof journey === "object") {
              hydrateStoreFromJourney(
                reduxDispatch,
                journey,
                persistedAppNumber,
                store.getState,
              );
              // If journey has progressed past lead, hydrate store and navigate to correct stage
              const resumeRoute = getResumeRouteFromJourney(journey);
              if (resumeRoute) {
                const resumeState =
                  resumeRoute === "/proposal"
                    ? getResumeStateFromJourney(journey)
                    : null;
                navigate(
                  resumeRoute,
                  resumeState ? { state: resumeState } : {},
                );
                return;
              }

              const stageLikeData =
                parseJsonObject(journey.stages) ||
                parseJsonObject(journey.currentStageData) ||
                parseJsonObject(journey.c_currentStageData);
              if (stageLikeData) {
                nextFormData = mergeWithoutBlankOverwrite(
                  nextFormData,
                  mergeCumulativeStagesOrdered(stageLikeData),
                );
              }

              const cumulativeRaw =
                journey.cummulativeJourneyData ||
                journey.c_cummulativeJourneyData ||
                journey.cummulative_journey_data ||
                journey.currentJourneyData ||
                journey.c_currentJourneyData ||
                null;

              if (cumulativeRaw && typeof cumulativeRaw === "string") {
                try {
                  const cumulativeObj = JSON.parse(cumulativeRaw);
                  nextFormData = mergeWithoutBlankOverwrite(
                    nextFormData,
                    mergeCumulativeStagesOrdered(cumulativeObj || {}),
                  );
                } catch {
                  // Ignore JSON parse errors and continue with default data
                }
              }

              const journeyAppNumber =
                journey.applicationNumber ||
                journey.application_number ||
                persistedAppNumber;
              const journeyCaseId =
                journey.caseId ||
                journey.c_caseId ||
                journey.case_number ||
                storedCaseId ||
                null;

              if (journeyAppNumber) {
                reduxDispatch(
                  journeyActions.setApplicationNumber(journeyAppNumber),
                );
                setStoredApplicationNumber(journeyAppNumber);
                setApplicationNumber(journeyAppNumber);
              }

              if (journeyCaseId) {
                reduxDispatch(journeyActions.setCaseId(journeyCaseId));
                setStoredCaseId(journeyCaseId);
              }

              // Restore currentStep when in personalize so we don't show step 1 again (e.g. was on step 2)
              if (mode === "personalize") {
                const stepIndex = resolveLeadStepIndexFromJourney(
                  journey,
                  def?.fieldGroups || [],
                );
                setCurrentStep(stepIndex);
                setCompletedSteps(
                  Array.from({ length: stepIndex }, (_, i) => i),
                );
              }
            }
          } catch {
            // Ignore journey fetch failures; still use persisted app number for subsequent submits
            setApplicationNumber(persistedAppNumber);
          }
        } else {
          // New journey: try to restore in-progress data from session storage
          try {
            const saved = sessionStorage.getItem(SESSION_FORM_KEY);
            if (saved) {
              const restored = JSON.parse(saved);
              if (restored && typeof restored === "object") {
                nextFormData = { ...initialData, ...restored };
              }
            }
          } catch {
            /* ignore corrupt data */
          }
        }

        const normalizedFormData = normalizeLeadFormDataOptionReferences(
          def,
          nextFormData,
        );
        setFormData(normalizedFormData);
        reduxDispatch(journeyActions.setLeadFormData(normalizedFormData));

        if (queryApplicationNumber && typeof window !== "undefined") {
          try {
            const encrypted = encryptJourneyQueryValue(queryApplicationNumber);
            if (
              encrypted &&
              queryApplicationNumberRaw &&
              encrypted !== queryApplicationNumberRaw &&
              window.history?.replaceState
            ) {
              const url = new URL(window.location.href);
              url.searchParams.set("applicationNumber", encrypted);
              const next = url.searchParams.toString();
              const nextUrl = next ? `${url.pathname}?${next}` : url.pathname;
              window.history.replaceState(null, document.title || "", nextUrl);
            }
          } catch {
            // ignore
          }
        }
      } catch (err) {
        setError(ERROR_MESSAGES.FORM_LOAD_ERROR);
        setApiErrorMsg(
          err?.response?.data?.message ||
          err?.message ||
          ERROR_MESSAGES.FORM_LOAD_ERROR,
        );
      } finally {
        setInitializingForm(false);
      }
    };

    loadForm();
  }, []);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      try {
        sessionStorage.setItem(SESSION_FORM_KEY, JSON.stringify(formData));
        // reduxDispatch({type: "leadFormData", payload: JSON.stringify(formData)})
      } catch (_) {
        /* quota */
      }
    }
  }, [formData]);

  useEffect(() => {
    const currentStepGroup = formDefinition?.fieldGroups?.[currentStep];
    if (!isResidencyLeadStep(currentStepGroup)) {
      setResidencyPincodeNoResults(false);
    }
  }, [currentStep, formDefinition]);

  useEffect(() => {
    const currentStepGroup = formDefinition?.fieldGroups?.[currentStep];
    if (!isAnnuitantStep(currentStepGroup)) return;
    const relationshipFieldName = getRelationshipFieldName(currentStepGroup);
    if (!relationshipFieldName) return;
    const currentRelationshipValue = formData?.[relationshipFieldName];
    if (!isBlankValue(currentRelationshipValue)) return;

    setFormData((prev) => {
      if (!isBlankValue(prev?.[relationshipFieldName])) return prev;
      const relationshipField = (currentStepGroup?.fields || []).find(
        isRelationshipField,
      );
      const resolvedSelf = mapOptionRawValueToReference(
        relationshipField,
        "self",
      );
      const next = { ...prev, [relationshipFieldName]: resolvedSelf };
      reduxDispatch(journeyActions.setLeadFormData(next));
      return next;
    });
  }, [currentStep, formDefinition, formData, reduxDispatch]);

  const clearSessionData = () => sessionStorage.removeItem(SESSION_FORM_KEY);

  const dismissAttentionModal = useCallback(() => {
    const rev = attentionModalRevertRef.current;
    if (rev) {
      const { fieldName, previousValue } = rev;
      setFormData((prev) => {
        const next = { ...prev, [fieldName]: previousValue };
        reduxDispatch(journeyActions.setLeadFormData(next));
        return next;
      });
      setErrors((prevE) => {
        const next = { ...prevE };
        delete next[fieldName];
        return next;
      });
      attentionModalRevertRef.current = null;
    }
    setShowAttentionModal(false);
  }, [reduxDispatch]);

  const confirmAttentionModal = useCallback(() => {
    attentionModalRevertRef.current = null;
    setShowAttentionModal(false);
  }, []);

   // Lead steps stay on the same route — scroll to top on each step change (basic → personal → LA, etc.).
  useEffect(() => {
    scrollToTopOnMount();
  }, [currentStep]);


  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (!name) return;
    let updatedValue = value;

    if (name === "fullName") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "").replace(/^\s+/, "");
    }
    if (name === "email" || name === "emailId" || name === "emailAddress") {
      updatedValue = value.replace(/\s/g, "");
    }
    const willShowAttention =
      isIndiaResidentField(name) && isNoValue(updatedValue);
    if (willShowAttention) {
      attentionModalRevertRef.current = {
        fieldName: name,
        previousValue: formData[name],
      };
    }

    setFormData((prev) => {
      const fieldDef = findLeadFieldDefinitionByDataKey(formDefinition, name);
      const storedValue = mapOptionRawValueToReference(fieldDef, updatedValue);
      const updatedData = {
        ...prev,
        [name]: storedValue,
      };

      reduxDispatch(journeyActions.setLeadFormData(updatedData));

      return updatedData;
    });

    if (formDefinition?.fieldGroups) {
      const allFields = formDefinition.fieldGroups
        .flatMap((g) => g.fields || [])
        .filter((field) =>
          shouldIncludeDynamicField(field, formDefinition, formData),
        );
      const fieldError = validateFieldByName(name, updatedValue, allFields);

      setErrors((prevE) => {
        const next = { ...prevE };
        if (fieldError) next[name] = fieldError;
        else delete next[name];
        return next;
      });
    }

    if (willShowAttention) {
      setShowAttentionModal(true);
    }
  };
  const validateCurrentStep = () => {
    if (!formDefinition?.fieldGroups?.[currentStep]) return true;
    const currentStepGroup = formDefinition.fieldGroups[currentStep];
    const isAnnuitantSelf = isAnnuitantSelfRelationship(
      formData,
      currentStepGroup,
    );
    const stepFields = (currentStepGroup.fields || []).filter(
      (field) =>
        shouldIncludeDynamicField(field, formDefinition, formData) &&
        !(isAnnuitantSelf && (isGenderField(field) || isDobField(field) || isNameField(field))),
    );
    const stepErrors = validateForm(formData, stepFields);

    const incomeError = incomeValidation(formData);
    if (incomeError?.fieldName) {
      stepErrors[incomeError.fieldName] = incomeError.message;
    }

    const annuitantAgeError = getAnnuitantAgeValidation(
      formData,
      currentStepGroup,
    );
    if (annuitantAgeError?.fieldName) {
      stepErrors[annuitantAgeError.fieldName] = annuitantAgeError.message;
    }
    setErrors((prev) => {
      const next = { ...prev, ...stepErrors };
      if (
        isAnnuitantStep(currentStepGroup) &&
        !annuitantAgeError &&
        getRelationshipFieldName(currentStepGroup)
      ) {
        delete next[getRelationshipFieldName(currentStepGroup)];
      }
      return next;
    });
    return Object.keys(stepErrors).length === 0;
  };

  const buildPayloadForCurrentStep = (
    targetStep = currentStep,
    sourceData = formData,
  ) => {
    const payload = {};
    const currentStepGroup = formDefinition?.fieldGroups?.[targetStep];
    const currentField = formDefinition?.fields?.[targetStep];
    const previousStepGroup =
      targetStep > 0 ? formDefinition?.fieldGroups?.[targetStep - 1] : null;

    const isAnnuitantSelf = isAnnuitantSelfRelationship(
      sourceData,
      currentStepGroup,
    );

    const stepFields = (currentStepGroup?.fields || []).filter(
      (field) =>
        shouldIncludeDynamicField(field, formDefinition, sourceData) &&
        !(isAnnuitantSelf && (isGenderField(field) || isDobField(field) || isNameField(field))),
    );

    stepFields.forEach((field) => {
      const name = field.fieldReference || field.name || field.fieldName;

      if (name && Object.prototype.hasOwnProperty.call(sourceData, name)) {
        let value = sourceData[name];


        // Map select/radio DDM Option* / reference codes to display labels
        // (e.g. "Female", "Salaried"). formParser already flattens label to a string,
        // so never read label.en_US here — that falls through to OptionXXXX values.
        if (field.options?.length) {
          const selectedOption = field.options.find((opt) => {
            const raw = String(value ?? "").trim();
            const optValue = String(opt?.value ?? "").trim();
            const optRef = String(opt?.reference ?? "").trim();
            const optLabel = getLocalizedOptionLabel(opt).trim();
            return (
              raw === optValue ||
              raw === optRef ||
              raw === optLabel ||
              raw.toLowerCase() === optLabel.toLowerCase()
            );
          });

          if (selectedOption) {
            value =
              getLocalizedOptionLabel(selectedOption) ||
              selectedOption.reference ||
              value;
          }
        }
        payload[name] = value;
      }
    });

    // Pincode autocomplete writes companion location fields that are not always
    // declared on the lead form definition. Persist them so quote/BI can resolve
    // @CUSCOM / @LI_STATE after resume without relying only on Redux.
    [
      "communicationState",
      "residencyState",
      "branchState",
      "city",
      "residencyCity",
      "communicationCity",
      "cityAndPincode",
    ].forEach((key) => {
      const value = sourceData?.[key];
      if (value != null && String(value).trim() !== "" && payload[key] == null) {
        payload[key] = value;
      }
    });

    if (isAnnuitantSelf) {
      const previousGenderFromStep = resolvePreviousStepValueByMatcher(
        sourceData,
        previousStepGroup,
        isGenderField,
      );
      const previousDobFromStep = resolvePreviousStepValueByMatcher(
        sourceData,
        previousStepGroup,
        isDobField,
      );
       const previousNameFromStep = resolvePreviousStepValueByMatcher(
        sourceData,
        previousStepGroup,
        isNameField,
      );
      const previousGender =
        previousGenderFromStep || getPrimaryLifeAssuredGender(sourceData);
      const previousDob =
        previousDobFromStep || getPrimaryLifeAssuredDob(sourceData);

      const previousName =
        previousNameFromStep || getPrimaryLifeAssuredName(sourceData);

      (currentStepGroup?.fields || []).forEach((field) => {
        const fieldName = field.fieldReference || field.name || field.fieldName;
        if (!fieldName) return;

        if (
          isGenderField(field) &&
          previousGender != null &&
          previousGender !== ""
        ) {
          payload[fieldName] = previousGender;
        }

        if (isDobField(field) && previousDob != null && previousDob !== "") {
          payload[fieldName] = previousDob;
        }

         if (isNameField(field) && previousName != null && previousName !== "") {
          payload[fieldName] = previousName;
        }
      });
    }

    if (targetStep === 0 && payload.annualIncome) {
      const { biCode, rangeLabel } = getIncomeRangeFromValue(
        payload.annualIncome,
      );
      payload.annualIncomeBiCode = biCode;
      payload.annualIncomeRangeLabel = rangeLabel;
    }

    // Prefer mapped option reference from payload (Yes/No -> reference).
    if (isSinglePayField(currentField)) {
      payload.isSinglePay = resolveIsSinglePayValue(
        payload?.isSinglePay,
        sourceData?.isSinglePay,
      );
    }

    payload.currentStage = resolveNischitLeadStage(
      targetStep,
      currentStepGroup,
      currentField?.fieldReference,
    );
    payload.productLob = JOURNEY_TYPES.NISCHIT_PENSION;
    payload.planName = journeyPlanName;

    const hasAllUtmParams = utmSourceParam && utmMediumParam && utmCampaignParam;
    if (targetStep === 0) {
      if (isCrosssellSource(utmSourceParam)) {
        payload.utmSource = "MbSb_Cross-sell";
        payload.utmMedium = utmMediumParam || "CPC";
        payload.utmCampaign = utmCampaignParam || "Google";
      } else if (hasAllUtmParams) {
        payload.utmSource = utmSourceParam;
        payload.utmMedium = utmMediumParam;
        payload.utmCampaign = utmCampaignParam;
      } else {
        payload.utmSource = "WebsiteJourney";
        payload.utmMedium = "CPC";
        payload.utmCampaign = "Google";
      }
    }

    if (payload.currentStage === "lead_basic") {
      return remapFields(payload, PROPOSER_BASIC_FORM_TO_PERSIST);
    }
    if (payload.currentStage === "lead_personal") {
      return remapFields(payload, PROPOSER_PERSONAL_FORM_TO_PERSIST);
    }
    if (isAnnuitantStep(currentStepGroup) || payload.currentStage === "lead_primary_annuitant") {
      return remapFields(payload, PRIMARY_ANNUITANT_FORM_TO_PERSIST);
    }
    return payload;
  };
  const saveFormDataToApi = async (isInitialSave = false) => {
    setSaving(true);
    setApiErrorMsg("");
    try {
      const dataToSubmit = buildPayloadForCurrentStep();
      const effectiveAppNumber = applicationNumber || storedApplicationNumber;
      if (effectiveAppNumber && !isInitialSave) {
        dataToSubmit.applicationNumber = effectiveAppNumber;
        addLeadOwnershipProof(dataToSubmit, formData, effectiveAppNumber);
      }

      const result = await submitFormData(dataToSubmit, "lead");
      if (effectiveAppNumber && !isInitialSave) {
        const persistedDob = resolveLeadDob(formData) || dataToSubmit?.proposerDob || dataToSubmit?.dateOfBirth;
        if (persistedDob) {
          syncOwnershipDobInSession(persistedDob, effectiveAppNumber);
        }
      }
      if (isInitialSave && result) {
        const extractedAppNum = extractApplicationNumberFromResponse(result);
        const extractedCaseId = extractCaseIdFromResponse(result);
        if (extractedAppNum) {
          setApplicationNumber(extractedAppNum);
          setStoredApplicationNumber(extractedAppNum);
          reduxDispatch(journeyActions.setApplicationNumber(extractedAppNum));
        }
        if (extractedCaseId) {
          reduxDispatch(journeyActions.setCaseId(extractedCaseId));
          setStoredCaseId(extractedCaseId);
        }
      }
      return result;
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.FORM_SUBMIT_ERROR;
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    const residencyStepGroup = formDefinition?.fieldGroups?.[currentStep];
    if (
      isResidencyLeadStep(residencyStepGroup) &&
      residencyPincodeNoResults
    ) {
      return;
    }

    // Only once the annuitant step is reached — before that the relationship
    // (and so whose gender/DOB applies) isn't known yet.
    const annuitantStepIndex = (formDefinition?.fieldGroups || []).findIndex(
      isAnnuitantStep,
    );
    if (
      annuitantStepIndex !== -1 &&
      currentStep >= annuitantStepIndex &&
      isPrimaryAnnuitantGenderAgeBlocked(formData, formDefinition)
    ) {
      setShowAgeCriteriaModal(true);
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    setLoading(true);

    saveStepData(currentStep, { ...formData });
    if (mode === "basic" && currentStep === 0) {
      try {
        const result = await saveFormDataToApi(true);
        reduxDispatch(journeyActions.setLeadFormData({ ...formData }));
        if (result) {
          const extractedAppNum = extractApplicationNumberFromResponse(result);
          const canonical = getTermJourneyCanonicalUrl();
          const leadDob = resolveLeadDob(formData);
          if (leadDob && extractedAppNum) {
            syncOwnershipDobInSession(leadDob, extractedAppNum);
          }
          const encryptedAppNum = extractedAppNum
            ? encryptJourneyQueryValue(extractedAppNum)
            : "";
          const url =
            extractedAppNum && encryptedAppNum
              ? `${canonical}?applicationNumber=${encodeURIComponent(encryptedAppNum)}`
              : canonical;
          window.location.href = url;
        }
      } catch (_) {
        /* handled in saveFormDataToApi */
      } finally {
        // IMPORTANT: this branch returns early (redirect path). Without this,
        // the loader can stay visible when navigation doesn't actually occur.
        setLoading(false);
      }
      return;
    }
    const totalSteps = formDefinition?.fieldGroups?.length || 0;
    const isLastStep = currentStep === totalSteps - 1;
    if (isLastStep) {
      try {
        await handleSubmit();
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      await saveFormDataToApi(currentStep === 0);
      setCurrentStep((prev) => prev + 1);
    } catch (_) {
      /* handled in saveFormDataToApi */
    } finally {
      setLoading(false);
    }
  };


  const handleStepClick = (stepIndex) => {
    if (completedSteps.includes(stepIndex) || stepIndex < currentStep) {
      setEditStepIndex(stepIndex);
    }
  };

  const handleEditSave = async (updatedData = {}, meta = {}) => {
    const hasChanges = Boolean(meta?.hasChanges);
    const targetStep = Number.isInteger(meta?.stepIndex)
      ? meta.stepIndex
      : editStepIndex;
    let mergedData = { ...formData, ...updatedData };
    mergedData = normalizeLeadFormDataOptionReferences(
      formDefinition,
      mergedData,
    );

    setFormData(mergedData);
    reduxDispatch(journeyActions.setLeadFormData(mergedData));
    // Quote/premium calculation reads leadResult.leadInput (not leadFormData),
    // so an edit here must also land there — otherwise a DOB/other edit made
    // after the lead is already completed is invisible to the quote page.
    if (leadResult) {
      reduxDispatch(
        journeyActions.setLeadResult({
          ...leadResult,
          leadInput: {
            ...(leadResult?.leadInput || {}),
            ...mergedData,
          },
        }),
      );
    }

    if (!hasChanges || !Number.isInteger(targetStep)) {
      return true;
    }

    try {
      const dataToSubmit = buildPayloadForCurrentStep(targetStep, mergedData);
      if (targetStep === 1) {
        dataToSubmit.planName = journeyPlanName;
      }
      const effectiveAppNumber = applicationNumber || storedApplicationNumber;
      if (effectiveAppNumber) {
        dataToSubmit.applicationNumber = effectiveAppNumber;
        addLeadOwnershipProof(dataToSubmit, mergedData, effectiveAppNumber);
      }
      await submitFormData(dataToSubmit, "lead");
      const persistedDob = resolveLeadDob(mergedData) || dataToSubmit?.dateOfBirth;
      if (persistedDob) {
        syncOwnershipDobInSession(persistedDob, effectiveAppNumber);
      }
      return true;
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.FORM_SUBMIT_ERROR;
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
      return false;
    }
  };


  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    const allFields =
      formDefinition?.fieldGroups?.flatMap((group, _groupIndex) => {
        const isAnnuitantSelf = isAnnuitantSelfRelationship(formData, group);
        return (group.fields || []).filter(
          (field) =>
            shouldIncludeDynamicField(field, formDefinition, formData) &&
            !(
              isAnnuitantSelf &&
              (isGenderField(field) || isDobField(field) || isNameField(field))
            ),
        );
      }) || [];
    const allErrors = validateForm(formData, allFields);
    const annuitantGroup = formDefinition?.fieldGroups?.find(isAnnuitantStep);
    const annuitantAgeError = getAnnuitantAgeValidation(
      formData,
      annuitantGroup,
    );
    if (annuitantAgeError?.fieldName) {
      allErrors[annuitantAgeError.fieldName] = annuitantAgeError.message;
    }
    if (
      !annuitantAgeError &&
      isPrimaryAnnuitantGenderAgeBlocked(formData, formDefinition)
    ) {
      setShowAgeCriteriaModal(true);
      return;
    }
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      const firstErrorStep = formDefinition.fieldGroups.findIndex((group) =>
        (group.fields || []).some(
          (f) => allErrors[f.fieldReference || f.name || f.fieldName],
        ),
      );
      if (firstErrorStep !== -1) setCurrentStep(firstErrorStep);
      return;
    }

    setSubmitting(true);
    setApiErrorMsg("");

    try {
      const finalLeadDetails = { ...formData };
      const annuitantGroup = formDefinition?.fieldGroups?.find(isAnnuitantStep);
      const isAnnuitantSelf = isAnnuitantSelfRelationship(
        finalLeadDetails,
        annuitantGroup,
      );
      if (isAnnuitantSelf && annuitantGroup) {
        const annuitantGenderField = (annuitantGroup.fields || []).find(
          isGenderField,
        );
        const annuitantDobField = (annuitantGroup.fields || []).find(
          isDobField,
        );
        const annuitantNameField = (annuitantGroup.fields || []).find(
          isNameField,
        );
        const annuitantGenderFieldName =
          annuitantGenderField?.fieldReference ||
          annuitantGenderField?.name ||
          annuitantGenderField?.fieldName;
        const annuitantDobFieldName =
          annuitantDobField?.fieldReference ||
          annuitantDobField?.name ||
          annuitantDobField?.fieldName;
        const annuitantName = annuitantNameField?.fieldReference ||
        annuitantNameField?.name ||
        annuitantNameField?.fieldName;

        const primaryGender = getPrimaryLifeAssuredGender(finalLeadDetails);
        const primaryDob = getPrimaryLifeAssuredDob(finalLeadDetails);
        const primaryName = getPrimaryLifeAssuredName(finalLeadDetails)
        if (annuitantGenderFieldName && primaryGender) {
          finalLeadDetails[annuitantGenderFieldName] = primaryGender;
        }
        if (annuitantDobFieldName && primaryDob) {
          finalLeadDetails[annuitantDobFieldName] = primaryDob;
        }
        if (annuitantName && primaryDob) {
          finalLeadDetails[annuitantName] = primaryName;
        }
      }

      const currentStagePayload = buildPayloadForCurrentStep();
      const resolvedIsSinglePay = resolveIsSinglePayValue(
        currentStagePayload?.isSinglePay,
        finalLeadDetails?.isSinglePay,
      );
      if (!isBlankValue(resolvedIsSinglePay)) {
        finalLeadDetails.isSinglePay = resolvedIsSinglePay;
        currentStagePayload.isSinglePay = resolvedIsSinglePay;
      }
      const dataToSubmit = {
        ...currentStagePayload,
        productLob: JOURNEY_TYPES.NISCHIT_PENSION,
        planName: journeyPlanName,
      };
      const effectiveAppNum = applicationNumber || storedApplicationNumber;
      const resumeDob = resolveLeadDob(finalLeadDetails);
      const redirectedToResumeStage = await syncAndRedirectIfJourneyAdvanced(effectiveAppNum, resumeDob);
      if (redirectedToResumeStage) return;
      if (effectiveAppNum) {
        dataToSubmit.applicationNumber = effectiveAppNum;
        addLeadOwnershipProof(dataToSubmit, finalLeadDetails, effectiveAppNum);
      }

      const result = await submitFormData(dataToSubmit, "lead");
      const persistedDob =
        resolveLeadDob(finalLeadDetails) || dataToSubmit?.dateOfBirth;
      if (persistedDob) {
        syncOwnershipDobInSession(persistedDob, effectiveAppNum);
      }

      // Nishchit is not gated by Super Suraksha term pincode flags.
      reduxDispatch(journeyActions.setPincodeTermPlanAvailability(null));

      clearSessionData();
      reduxDispatch(journeyActions.setLeadFormData(finalLeadDetails));
      sessionStorage.setItem("leadCompleted", "true");
      handleLeadComplete({
        result,
        applicationNumber: effectiveAppNum,
        formData: finalLeadDetails,
        pincodeTermPlanAvailability: null,
      });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.FORM_SUBMIT_ERROR;
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentStepFields = () =>
    (formDefinition?.fieldGroups?.[currentStep]?.fields || []).filter(
      (field) => {
        if (!shouldIncludeDynamicField(field, formDefinition, formData))
          return false;
        const currentStepGroup = formDefinition?.fieldGroups?.[currentStep];
        const isAnnuitantSelf = isAnnuitantSelfRelationship(
          formData,
          currentStepGroup,
        );
        return !(
          isAnnuitantSelf &&
          (isGenderField(field) || isDobField(field) || isNameField(field))
        );
      },
    );

  const getSteps = () => {
    if (!formDefinition?.fieldGroups) return [];
    return formDefinition.fieldGroups.map((group, index) => ({
      label: group.label || group.title || group.name || `Step ${index + 1}`,
      index,
    }));
  };

  const getUserName = () => {
    const name = formData.fullName || formData.name || formData.firstName || "";
    return name ? name.split(" ")[0] : "there";
  };

  if (loading) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader />
      </div>
    );
  }

  if (initializingForm) {
    return (
      <div className="insurance-journey-lead">
        <div className="lead-container">
          <div
            className="lead-form-loading"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading form"
          >
            <BrandLoader />
          </div>
        </div>
      </div>
    );
  }

  if (error || !formDefinition) {
    return (
      <div className="insurance-journey-lead">
        <div className="lead-container">
          <div className="error-state">
            <h3>Unable to load form</h3>
            <p>{error || ERROR_MESSAGES.FORM_LOAD_ERROR}</p>
            {apiErrorMsg && apiErrorMsg !== error && (
              <p className="error-details">{apiErrorMsg}</p>
            )}
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const allSteps = getSteps();
  const visibleSteps = mode === "personalize" ? allSteps : [];
  const currentFields = getCurrentStepFields();
  const totalSteps = formDefinition.fieldGroups.length;
  const isLastStep = currentStep === totalSteps - 1;
  const userName = getUserName();
  const currentStepGroup = formDefinition?.fieldGroups?.[currentStep] || null;
  const currentStepLabel = allSteps[currentStep]?.label || "";
  const stepCopy = getCurrentStepCopy(currentStepGroup, currentStepLabel);
  const stepTitle =
    stepCopy.title || getStepTitle(currentStep, currentStepLabel);
  const stepSubtitle = stepCopy.subtitle || "";
  const stepBubbleText = stepCopy.bubble || "";
  const showResidencyHeader = stepCopy.referenceKey === ("residency");
  const isResidencyPincodeBlocked =
    showResidencyHeader && residencyPincodeNoResults;
  const showAdditionalHeader = stepCopy.referenceKey === ("additional");
  const showDefaultStepHeader =
    currentStep !== 0 && !showResidencyHeader && !showAdditionalHeader;
  const shouldShowStepSubtitle =
    Boolean(stepSubtitle) &&
    stepSubtitle.trim().toLowerCase() !== stepTitle.trim().toLowerCase();
  const isContinueDisabled = submitting || saving;
  const { fieldName: consentFieldName, isHidden: isConsentHidden } =
    getConsentTermsFieldMeta(formDefinition);
  const showTermsAndConditions = Boolean(consentFieldName) && isConsentHidden;
  const requiresTermsConsent = currentStep === 0 && showTermsAndConditions;
  const isSubmitDisabled =
    isContinueDisabled ||
    (requiresTermsConsent && !formData[consentFieldName]) ||
    isResidencyPincodeBlocked;
  const buttonText = getButtonText(submitting || saving, isLastStep);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsChecked(checked);
    if (consentFieldName) {
      setFormData((prev) => {
        const next = { ...prev, [consentFieldName]: checked };
        reduxDispatch(journeyActions.setLeadFormData(next));
        return next;
      });
    }
  };

  const minAge = () => (currentStepLabel === "Annuitant" ? 30 : 18);
  const maxAge = () => (75);

  return (
    <div className="insurance-journey-lead">
      <div className="lead-container">
        {currentStep !== 0 && (
          <StepNavigation
            steps={visibleSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            completedSteps={completedSteps}
          />
        )}
        {currentStep !== 0 && (
          <div className="welcome-message">
            <img src={textImage} alt="life cover in 2 minutes" />
            <h1 className="welcome-text regular-body bold Secondary-Grey-Shades-950 ">
              Hey, {userName}! Let&apos;s build your perfect life cover in 2
              mins!
            </h1>
          </div>
        )}

        <div
          className={`form-modal ${currentStep !== 0 ? "modal-fom-box" : ""}`}
        >
          {currentStep !== 0 && (
            <div className="bg-form-img">
              <img src={backgroundImg} alt="journey-bg-icon" />
            </div>
          )}
          <form
            className="dynamic-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            {showAdditionalHeader && (
              <div className="form-intro">
                <p>
                  Hey, {userName}! you are{" "}
                  <span className="green-text">almost there</span> - Just a few
                  quick details to personalise your plan.
                </p>
              </div>
            )}
            {showResidencyHeader && (
              <div className="header-top">
                <h6 className="small-body semibold m-0">
                  Let’s personalise your plan!
                </h6>
                <div className="timer-header">
                  <h3 className="form-title bold">{stepTitle}</h3>
                </div>
              </div>
            )}
            {showAdditionalHeader && (
              <div className="header-top">
                <h6 className="small-body semibold m-0">
                  Let’s personalise your plan!
                </h6>
                <div className="timer-header">
                  <h3 className="form-title bold">{stepTitle}</h3>
                  <span className="form-subtitle-time">
                    <span>30 seconds away</span>
                    <img src={clockTimer} width="16" height="16" alt="clocktimer-icon" />
                  </span>
                </div>
              </div>
            )}
            {showDefaultStepHeader && (
              <div className="header-top">
                <h6 className="small-body semibold m-0">
                  Let’s personalise your plan!
                </h6>
                <div className="timer-header">
                  <h3 className="form-title bold">{stepTitle}</h3>
                </div>
              </div>
            )}

            <div
              className={`form-step ${currentStep === 0 ? "basic-from" : ""} ${showAdditionalHeader ? "residency-form" : ""}`}
            >
              {shouldShowStepSubtitle && (
                <div className="header-top">
                  <h6 className="small-body semibold m-0">
                    Let’s personalise your plan!
                  </h6>
                  <h3 className="form-question bold">{stepSubtitle}</h3>
                </div>
              )}
              {currentFields.map((field, index) => {
                const fieldName =
                  field.fieldReference || field.name || field.fieldName;
                return (
                  <DynamicFormField
                    key={fieldName || index}
                    field={field}
                    value={formData[fieldName]}
                    onChange={handleFieldChange}
                    onPincodeSuggestionsChange={
                      showResidencyHeader
                        ? handlePincodeSuggestionsChange
                        : undefined
                    }
                    error={errors[fieldName] || null}
                    disabled={submitting}
                    useDdMmYyyyForDob
                    minAge={minAge()}
                    maxAge={maxAge()}
                    autoFocus={index === 0}
                  />
                );
              })}
            </div>

            <div className="form-actions">
              {currentStep !== 0 && (
                <>
                  {stepBubbleText && (
                    <div className="info-bubble">
                      <div className="info-bubble-box">
                        <p>{stepBubbleText}</p>
                      </div>
                      <div className="bubble-illustration">
                        <svg
                          width="80"
                          height="80"
                          viewBox="0 0 80 80"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M23.0574 30.7196C22.2693 29.9315 15.8257 23.9193 16.6477 22.9682C17.0757 22.4417 19.3753 23.8208 19.3753 23.8208C21.2163 25.7263 23.0913 27.6625 24.9629 29.5681C25.3909 29.3065 25.8189 29.0755 26.2774 28.8446C25.948 26.3139 25.52 25.3289 27.8229 24.2148C30.486 22.8697 28.1184 21.6842 28.0539 19.5511C27.9554 17.4825 29.664 17.153 30.6829 14.0313C32.4595 8.67813 36.5695 10.3187 37.5545 9.99265C37.7515 9.89414 39.4941 7.95456 43.441 9.0076C47.9791 10.2236 51.2977 14.5919 51.2977 19.3885C51.2977 21.328 51.6578 21.1616 52.9078 21.7866C56.4574 23.4951 50.8697 26.2193 54.9119 25.9917C57.6735 25.8592 60.007 27.6018 59.3175 30.326C58.8895 32.0685 60.5675 30.3599 61.651 32.9211C61.9805 33.8076 61.9465 35.2546 61.3215 35.5162C67.0077 40.343 71.4128 46.651 73.7502 53.7771C75.7237 59.7213 75.2957 65.1727 68.6551 68.5562C61.4234 72.2994 52.809 70.3293 46.4951 65.207C46.7566 67.1126 45.9346 69.4427 43.6011 70.2307C40.8735 71.1513 37.059 71.4467 34.2976 70.6893C33.6387 70.5262 9.8037 72.9243 5.76197 59.4902C3.09892 50.5876 10.5276 38.3049 23.055 30.7162L23.0574 30.7196ZM24.7659 32.5606C26.6714 32.9886 28.9744 33.1516 28.7774 33.9737C28.6144 34.7617 27.0994 34.1061 25.5879 33.7766C25.9174 37.1598 29.236 37.0273 30.6185 37.6184C33.611 38.899 34.2666 42.6421 35.418 44.3201L37.1266 41.0694C34.1341 39.2624 33.8386 34.8296 31.1415 33.2196C29.9255 32.4961 24.236 31.579 24.0084 32.136C23.9744 32.2345 24.2699 32.4316 24.7659 32.564L24.7659 32.5606ZM36.7972 13.5424C35.2856 13.8718 34.6946 15.6789 34.3651 17.024L35.1871 18.3385C35.5166 18.3385 35.8461 18.4031 36.1721 18.5355C37.2557 16.63 40.0852 17.024 40.6117 19.1266C41.4982 19.1605 42.5173 19.2896 43.5057 19.5206C40.1531 17.024 39.6267 12.9853 36.7972 13.5424ZM32.8535 19.9146C33.6416 21.5247 34.9901 19.259 33.9711 18.009C33.1831 16.99 32.0315 18.3046 32.8535 19.9146ZM39.6606 19.4221C39.1341 16.8915 35.3874 19.1605 37.3576 20.8011C38.3767 21.6537 39.9221 20.7366 39.6606 19.4221ZM45.3161 21.0967C44.0661 20.6042 42.1606 20.2102 40.649 20.1116C40.1225 23.1348 35.5845 22.5097 35.8494 19.4866C35.6864 19.3881 35.4554 19.3542 35.2584 19.3236C35.2244 20.7026 34.3719 21.6572 33.1864 21.3617C32.2999 27.4045 36.804 31.1513 41.0431 27.965C41.8991 27.3094 42.6871 26.4874 43.4107 25.6314C43.4752 25.499 43.4446 23.7599 44.3312 24.3169C45.1872 24.8434 47.2557 23.3963 45.3161 21.0967ZM42.6871 27.863C40.7475 29.7345 38.577 30.7876 36.0465 29.7345C36.308 33.1176 37.0315 36.5008 38.1491 39.8197C40.0887 37.4216 42.9486 31.2156 42.6871 27.863ZM53.6688 68.2299C61.196 71.022 71.0601 68.7224 73.3627 62.2856C74.3818 59.4596 73.5258 56.2429 72.8362 54.0757C68.5631 41.1983 57.4832 31.0496 44.331 27.8323C46.3691 29.9688 50.1836 36.3716 48.2747 36.2731L44.9867 36.1101C45.3162 38.8683 43.5736 41.2663 40.8766 42.0883C45.7102 45.0775 50.7409 48.0666 56.0331 50.2677C62.0182 52.7983 62.2797 49.5781 63.1017 50.5632C63.8252 51.4497 60.9312 53.4538 55.6391 51.1882C50.0821 48.8546 44.8548 45.7669 39.8235 42.6148C39.1645 44.5203 38.1149 46.2595 36.6679 47.6725C39.4634 56.7386 45.0171 64.9821 53.6653 68.2334L53.6688 68.2299ZM43.2784 69.249C45.7444 68.4269 45.7105 65.5703 45.4489 64.8808C45.1874 64.2558 43.8388 63.0737 43.6079 62.9412C41.8994 62.0886 39.7627 64.8468 39.3994 63.4677C39.1684 62.5812 40.9448 62.9106 40.8803 62.1226C40.7478 60.7774 35.6187 63.8651 35.3572 63.9636L35.0617 69.8095C37.7892 70.3359 40.6187 70.1389 43.2818 69.2524L43.2784 69.249ZM6.75318 59.1637C8.85579 66.2256 17.6367 69.9385 31.5767 69.7411C31.5121 67.4755 31.7091 65.2744 32.1677 63.1718C27.5651 62.2513 22.4364 62.2513 19.0833 58.7696C15.4998 55.0264 17.6024 49.6392 18.7539 50.5257C18.9848 50.6888 19.0154 51.0183 18.8524 51.2153C17.3069 53.2839 18.1289 56.3375 19.8069 58.0801C21.0569 59.3607 22.7654 60.2811 25.133 60.9061L22.8639 40.7061L23.3259 40.1491L31.8415 39.7551C30.3606 37.9819 29.1445 38.376 27.5005 37.751C25.8225 37.0614 24.6404 35.3189 24.5759 33.5152C23.4244 33.2197 22.8979 32.7272 23.0643 31.8746C11.393 39.1674 4.29041 50.9911 6.75649 59.1711L6.75318 59.1637Z"
                            fill="#AE272C"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </>
              )}
              {currentStep === 0 && showTermsAndConditions && (
                <div className="terms-and-conditions">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={Boolean(formData[consentFieldName])}
                      onChange={handleCheckboxChange}
                    />

                    <p className="terms-and-conditions-text">
                      I agree to the{" "}
                      <button
                        type="button"
                        className="terms-and-conditions-link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowTermsModal(true);
                        }}
                      >
                        Terms &amp; Conditions
                      </button>{" "}
                      and allow INLIC to contact me regarding my request
                    </p>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`btn-continue btn primary-button regular ${isSubmitDisabled ? "disabled" : ""}`}
              >
                {buttonText}
              </button>
            </div>
          </form>
        </div>
        {visibleSteps && currentStep !== 0 && (
          <div className="bottom-pagination-dots">
            {visibleSteps.map((step, index) => (
              <span
                className={`step-dot ${completedSteps.includes(index) ? "completed" : ""} ${index === currentStep ? "active" : ""}`}
                key={step.label}
              ></span>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
      </Modal>

      <Modal
        isOpen={showAgeCriteriaModal}
        onClose={() => setShowAgeCriteriaModal(false)}
        title="Product Eligibility Not Met"
        type="error"
        btnTitle="OK"
      >
        <p>The information provided does not satisfy the eligibility requirements for this product. Please check the details entered and try again.</p>

      </Modal>

      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        type="error"
        btnTitle="Close"
      >
        <p>{apiErrorMsg || ERROR_MESSAGES.FORM_SUBMIT_ERROR}</p>
      </Modal>

      {showTermsModal && (
        <div class="term-modal">
          <div class="term-modal-body">
            <h6>Terms &amp; Conditions</h6>
            <button class="close-button" onClick={() => setShowTermsModal(false)}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                  fill="#3C3D3D"
                ></path>
              </svg>
            </button>
            <div class="modal-content">
              <p>
                By submitting my details, I override my NDNC registration and
                authorize{" "}
                <strong>IndusInd Nippon Life Insurance Company Limited</strong>,
                its third parties and its representatives to contact me through
                call, RCS, WhatsApp or E-Mail.
              </p>
              <br />
              <p>
                I further consent to permit IndusInd Nippon Life Insurance
                Company Limited to share my confidential information with its
                third parties and its representatives:
              </p>
              <ul>
                <li>For contacting me</li>
                <li>For evaluating</li>
                <li>For processing this proposal</li>
              </ul>
              <p>All communications issued by the insurer relating to subscriptions, promotional updates, marketing communications, notifications, or similar services shall clearly provide customers with an easily accessible option to opt out or unsubscribe from such communications at any time, wherever applicable.</p>
            </div>
          </div>
        </div>
      )}


      <AttentionModal
        isOpen={showAttentionModal}
        onConfirm={confirmAttentionModal}
        onDismiss={dismissAttentionModal}
        type="error"
        btnTitle="OK"
        bottomBtn="Cancel"
      >
        <img src={attentionImg} alt="confirmAttentionModal" width="60" height="56" />
        <h4 className="modal-title">Attention</h4>
        <p>
          You have selected your tax residence outside India. You will be
          required to fill up FATCA/CRS declaration format, sent to your
          registered email ID, and submit it along with your required documents.
          <br />
        </p>
      </AttentionModal>

      <EditStepModal
        isOpen={editStepIndex !== null}
        onClose={() => setEditStepIndex(null)}
        stepIndex={editStepIndex}
        title={
          editStepIndex !== null
            ? allSteps[editStepIndex]?.label || "Edit Details"
            : "Edit Details"
        }
        formData={formData}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default InsuranceJourneyLead;
